import { useEffect, useRef, useState, type RefObject } from 'react';
import type { MusicVideoClip, MusicVideoClipSource, MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { FootageView } from '../footage/footageView';
import { clipProgress, loopedSourceTime, moveTransform, needsResync, previewClips } from './previewClock';
import type { PreviewTransport } from './usePreviewTransport';

/** What each of the two video elements holds, for the render. */
export interface DualVideoView {
  /** The clip id that each element holds, or null. */
  slots: [string | null, string | null];
  /** The element on screen. */
  active: 0 | 1;
}

interface Slot {
  clipId: string | null;
  url: string | null;
}

interface UseDualVideoProps {
  videos: readonly [RefObject<HTMLVideoElement | null>, RefObject<HTMLVideoElement | null>];
  doc: MusicVideoDoc;
  lookup: (source: MusicVideoClipSource) => FootageView | null;
  transport: PreviewTransport;
}

/** Puts a link into a video element and starts it at `atSec` when its length is known. */
function loadSlot(video: HTMLVideoElement | null, url: string | null, atSec: number): void {
  if (video === null) {
    return;
  }
  if (url === null) {
    video.removeAttribute('src');
    video.load();
    return;
  }
  if (video.getAttribute('src') !== url) {
    video.src = url;
    video.addEventListener(
      'loadedmetadata',
      () => {
        video.currentTime = atSec;
      },
      { once: true }
    );
    return;
  }
  video.currentTime = atSec;
}

/** The place in the footage, inside the length of the file when the browser knows it. */
function inFile(video: HTMLVideoElement, sec: number): number {
  const { duration } = video;
  return Number.isFinite(duration) && duration > 0 ? Math.min(sec, Math.max(0, duration - 0.05)) : sec;
}

/**
 * The two muted `<video>` elements of the live preview (spec §4.6). On each animation frame it
 * reads the master clock, finds the clip on screen and the next clip, and:
 * - shows the element that holds the clip on screen; the other element loads the next clip, so a
 *   cut swaps two ready elements;
 * - seeks the element to `inSec + (t - startSec) * speed` when it is more than 0.15 s off;
 * - plays and pauses it with the master, at the speed of the clip, with the camera move.
 * React renders again only when an element gets another clip or the swap happens.
 */
export function useDualVideo({ videos, doc, lookup, transport }: UseDualVideoProps): DualVideoView {
  const [view, setView] = useState<DualVideoView>({ slots: [null, null], active: 0 });
  const live = useRef({ doc, lookup, transport });
  live.current = { doc, lookup, transport };

  useEffect(() => {
    const slots: [Slot, Slot] = [
      { clipId: null, url: null },
      { clipId: null, url: null },
    ];
    let active: 0 | 1 = 0;
    let frame = 0;

    const assign = (index: 0 | 1, clip: MusicVideoClip | null, atSec: number): boolean => {
      const url = clip === null ? null : (live.current.lookup(clip.source)?.previewUrl ?? null);
      const slot = slots[index];
      if (slot.clipId === (clip?.id ?? null) && slot.url === url) {
        return false;
      }
      slots[index] = { clipId: clip?.id ?? null, url };
      loadSlot(videos[index].current, url, atSec);
      return true;
    };

    const tick = () => {
      const { doc: nowDoc, transport: nowTransport } = live.current;
      const sec = nowTransport.livePositionSec();
      const { current, next } = previewClips(nowDoc, sec);
      let changed = false;
      if (current !== null) {
        const url = live.current.lookup(current.source)?.previewUrl ?? null;
        const holding = slots.findIndex((slot) => slot.clipId === current.id && slot.url === url);
        let index: 0 | 1 = holding === 1 ? 1 : 0;
        // A clip longer than the rest of its source loops, as the server renders it (`clipLoopParts`).
        const sourceSec = live.current.lookup(current.source)?.durationSec ?? null;
        if (holding < 0) {
          index = active === 0 ? 1 : 0;
          changed = assign(index, current, loopedSourceTime(current, sec, sourceSec)) || changed;
        }
        if (index !== active) {
          videos[active].current?.pause();
          active = index;
          changed = true;
        }
        const video = videos[index].current;
        if (video !== null && url !== null) {
          const wanted = inFile(video, loopedSourceTime(current, sec, sourceSec));
          if (needsResync(video.currentTime, wanted)) {
            video.currentTime = wanted;
          }
          video.playbackRate = current.speed;
          if (nowTransport.isPlaying && video.paused) {
            video.play().catch(() => undefined);
          } else if (!nowTransport.isPlaying && !video.paused) {
            video.pause();
          }
          video.style.transform = moveTransform(current.move, clipProgress(current, sec));
        }
        const other: 0 | 1 = index === 0 ? 1 : 0;
        if (next !== null) {
          changed = assign(other, next, next.inSec) || changed;
        }
      }
      if (changed) {
        setView({ slots: [slots[0].clipId, slots[1].clipId], active });
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      videos.forEach((ref) => ref.current?.pause());
    };
    // The loop reads the newest props through `live`; it starts once per pair of elements.
  }, [videos]);

  return view;
}
